/**
 * TODO(color-lab): 暗色基调临时调整工具，定稿后整体移除：
 * 1. 删除本目录 src/components/ColorLabTuner/
 * 2. 删除 src/views/Main/index.tsx 中的 import 与 <ColorLabTuner /> 挂载行
 *
 * 运行机制：运行时向 document.documentElement 写入内联 CSS 变量覆盖
 * （仅在 dark 模式生效），不改 colorTokenRegistry 源码；
 * 覆盖值持久化在 localStorage（fett-color-lab:bg-overrides）。
 * 仅非生产环境渲染。
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import {
  BgColorsOutlined,
  CloseOutlined,
  CopyOutlined,
  ImportOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useThemeContext } from 'components/ThemesProvider';
import ColorPicker from './ColorPicker';
import { normalizeHex } from './colorMath';
import { DEFAULT_DARK, PRESETS, TUNABLE_TOKENS } from './presets';

const STORAGE_KEY = 'fett-color-lab:bg-overrides';

// 暗色Lab 浮动入口暂时隐藏；需要调试时改为 false 即可恢复
const COLOR_LAB_ENTRY_HIDDEN = true;

type Overrides = Record<string, string>;

const readStoredOverrides = (): Overrides => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

// 工具自身使用固定配色，避免调整 token 时工具面板跟着变色、无法阅读
const FloatingButton = styled.button`
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 9999;
  display: flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 12px;
  border: 1px solid #3a3f47;
  border-radius: 17px;
  background: rgba(24, 26, 30, 0.96);
  color: #e6e8eb;
  font-size: 12px;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);

  &:hover {
    border-color: #5b8cff;
    color: #5b8cff;
  }
`;

const Panel = styled.div<{ $left?: number; $top?: number }>`
  position: fixed;
  ${({ $left, $top }) =>
    $left !== undefined && $top !== undefined
      ? `left: ${$left}px; top: ${$top}px;`
      : 'right: 16px; bottom: 16px;'}
  z-index: 9999;
  width: 264px;
  max-height: calc(100vh - 32px);
  display: flex;
  flex-direction: column;
  border: 1px solid #3a3f47;
  border-radius: 10px;
  background: rgba(24, 26, 30, 0.97);
  color: #e6e8eb;
  font-size: 12px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.5);
  overflow: hidden;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border-bottom: 1px solid #2e3238;
  cursor: move;
  user-select: none;
`;

const HeaderTitle = styled.span`
  flex: 1;
  font-weight: 600;
`;

const ModeTag = styled.span<{ $dark: boolean }>`
  padding: 1px 6px;
  border-radius: 8px;
  font-size: 10px;
  background: ${({ $dark }) => ($dark ? '#26364f' : '#4a4436')};
  color: ${({ $dark }) => ($dark ? '#8fb8ff' : '#ffd98a')};
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #9aa0a8;
  cursor: pointer;

  &:hover {
    background: #2e3238;
    color: #e6e8eb;
  }
`;

const PanelBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 8px 10px 10px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #3a3f47;
    border-radius: 2px;
  }
`;

const SectionLabel = styled.div`
  margin: 6px 0 4px;
  color: #9aa0a8;
  font-size: 11px;
`;

const PresetChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const Chip = styled.button<{ $active: boolean }>`
  padding: 2px 8px;
  border: 1px solid ${({ $active }) => ($active ? '#5b8cff' : '#3a3f47')};
  border-radius: 10px;
  background: ${({ $active }) => ($active ? 'rgba(91, 140, 255, 0.16)' : 'transparent')};
  color: ${({ $active }) => ($active ? '#8fb8ff' : '#c6cad0')};
  font-size: 11px;
  cursor: pointer;

  &:hover {
    border-color: #5b8cff;
  }
`;

const TokenRow = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 26px;
  padding: 0 6px;
  border: none;
  border-radius: 4px;
  background: ${({ $active }) => ($active ? '#2e3238' : 'transparent')};
  color: #e6e8eb;
  font-size: 12px;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: #2e3238;
  }
`;

const Swatch = styled.span`
  width: 14px;
  height: 14px;
  flex: 0 0 14px;
  border-radius: 3px;
  border: 1px solid rgba(255, 255, 255, 0.25);
`;

const TokenLabel = styled.span`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const HexValue = styled.span`
  color: #9aa0a8;
  font-size: 10px;
  font-family: monospace;
`;

const PickerSection = styled.div`
  margin-top: 6px;
  padding: 8px;
  border: 1px solid #2e3238;
  border-radius: 6px;
  background: #1b1e22;
`;

const PickerTitle = styled.div`
  margin-bottom: 6px;
  color: #9aa0a8;
  font-size: 11px;
`;

const PanelFooter = styled.div`
  display: flex;
  gap: 6px;
  padding: 8px 10px;
  border-top: 1px solid #2e3238;
`;

const FooterButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  height: 24px;
  padding: 0 4px;
  border: 1px solid #3a3f47;
  border-radius: 4px;
  background: transparent;
  color: #c6cad0;
  font-size: 10px;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    border-color: #5b8cff;
    color: #8fb8ff;
  }
`;

const LightHint = styled.div`
  margin-top: 6px;
  padding: 4px 6px;
  border-radius: 4px;
  background: rgba(255, 217, 138, 0.1);
  color: #ffd98a;
  font-size: 11px;
`;

const ImportSection = styled.div`
  padding: 8px 10px;
  border-top: 1px solid #2e3238;
`;

const ImportTextarea = styled.textarea`
  width: 100%;
  height: 88px;
  padding: 6px 8px;
  border: 1px solid #3a3f47;
  border-radius: 4px;
  background: #141619;
  color: #e6e8eb;
  font-size: 11px;
  font-family: monospace;
  resize: none;
  outline: none;

  &:focus {
    border-color: #5b8cff;
  }
`;

const ImportError = styled.div`
  margin-top: 4px;
  color: #ff9a9a;
  font-size: 11px;
`;

const ImportActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 6px;
`;

const ColorLabTuner = () => {
  const { resolvedMode } = useThemeContext();
  const [open, setOpen] = useState(false);
  const [overrides, setOverrides] = useState<Overrides>(readStoredOverrides);
  const [selectedToken, setSelectedToken] = useState(TUNABLE_TOKENS[0].name);
  const [copied, setCopied] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importText, setImportText] = useState('');
  const [importError, setImportError] = useState<string | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    pointerId: number;
    baseLeft: number;
    baseTop: number;
    startX: number;
    startY: number;
  } | null>(null);

  const effective = useMemo<Overrides>(
    () =>
      Object.fromEntries(
        TUNABLE_TOKENS.map(({ name }) => [name, overrides[name] ?? DEFAULT_DARK[name]]),
      ),
    [overrides],
  );

  const activePresetId = useMemo(() => {
    const matched = PRESETS.find((preset) =>
      TUNABLE_TOKENS.every(({ name }) => overrides[name] === preset.colors[name]),
    );
    if (matched) return matched.id;
    if (TUNABLE_TOKENS.every(({ name }) => !overrides[name])) return 'current';
    return 'custom';
  }, [overrides]);

  // 实时应用：仅在 dark 模式写内联变量覆盖，light 模式保持原样
  useEffect(() => {
    const root = document.documentElement;
    TUNABLE_TOKENS.forEach(({ name }) => {
      const value = resolvedMode === 'dark' ? overrides[name] : undefined;
      if (value) root.style.setProperty(name, value);
      else root.style.removeProperty(name);
    });
  }, [overrides, resolvedMode]);

  // 持久化，刷新后保持
  useEffect(() => {
    try {
      if (Object.keys(overrides).length > 0) {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // 隐私模式等场景下忽略
    }
  }, [overrides]);

  const applyPreset = useCallback((presetId: string) => {
    const preset = PRESETS.find((item) => item.id === presetId);
    if (!preset) return;
    setOverrides(preset.id === 'current' ? {} : { ...preset.colors });
  }, []);

  const handleTokenColorChange = useCallback(
    (hex: string) => {
      setOverrides((prev) => ({ ...prev, [selectedToken]: hex }));
    },
    [selectedToken],
  );

  const handleReset = useCallback(() => {
    setOverrides({});
  }, []);

  const handleImportApply = useCallback(() => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(importText);
    } catch {
      setImportError('JSON 解析失败，请检查格式');
      return;
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      setImportError('需要是 { "--fett-bg-xxx": "#rrggbb" } 这样的对象');
      return;
    }
    const tokenNames = new Set(TUNABLE_TOKENS.map(({ name }) => name));
    const next: Overrides = {};
    const invalid: string[] = [];
    Object.entries(parsed as Record<string, unknown>).forEach(([name, value]) => {
      if (!tokenNames.has(name)) return;
      const hex = typeof value === 'string' ? normalizeHex(value) : null;
      if (hex) next[name] = hex;
      else invalid.push(name);
    });
    if (Object.keys(next).length === 0) {
      setImportError('没有可识别的 token 色值');
      return;
    }
    setOverrides(next);
    if (invalid.length > 0) {
      // 部分色值非法：保持输入框打开并提示，已应用的部分仍然生效
      setImportError(`已应用，以下色值非法被跳过：${invalid.join('、')}`);
      return;
    }
    setImportError(null);
    setImporting(false);
    setImportText('');
  }, [importText]);

  const handleCopy = useCallback(() => {
    const text = JSON.stringify(effective, null, 2);
    const done = () => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(done, done);
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      done();
    }
  }, [effective]);

  if (COLOR_LAB_ENTRY_HIDDEN || import.meta.env.VITE_ENV === 'production') {
    return null;
  }

  if (!open) {
    return (
      <FloatingButton onClick={() => setOpen(true)} title="暗色基调调整">
        <BgColorsOutlined />
        暗色Lab
      </FloatingButton>
    );
  }

  return (
    <Panel ref={panelRef} $left={pos?.left} $top={pos?.top}>
      <PanelHeader
        onPointerDown={(event) => {
          if (event.button !== 0 || !panelRef.current) return;
          // 标题栏内的按钮（收起等）不触发拖拽，否则 setPointerCapture 会吃掉 click
          if ((event.target as HTMLElement).closest('button')) return;
          const rect = panelRef.current.getBoundingClientRect();
          dragRef.current = {
            pointerId: event.pointerId,
            baseLeft: rect.left,
            baseTop: rect.top,
            startX: event.clientX,
            startY: event.clientY,
          };
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={(event) => {
          const drag = dragRef.current;
          if (!drag || drag.pointerId !== event.pointerId) return;
          const left = Math.min(
            Math.max(0, drag.baseLeft + event.clientX - drag.startX),
            window.innerWidth - 120,
          );
          const top = Math.min(
            Math.max(0, drag.baseTop + event.clientY - drag.startY),
            window.innerHeight - 60,
          );
          setPos({ left, top });
        }}
        onPointerUp={(event) => {
          if (dragRef.current?.pointerId === event.pointerId) dragRef.current = null;
        }}
      >
        <BgColorsOutlined />
        <HeaderTitle>暗色基调 Lab</HeaderTitle>
        <ModeTag $dark={resolvedMode === 'dark'}>
          {resolvedMode === 'dark' ? 'Dark' : 'Light'}
        </ModeTag>
        <IconButton title="收起" onClick={() => setOpen(false)}>
          <CloseOutlined />
        </IconButton>
      </PanelHeader>
      <PanelBody>
        {resolvedMode !== 'dark' && <LightHint>当前为浅色模式，调整仅在深色模式下生效</LightHint>}
        <SectionLabel>预设</SectionLabel>
        <PresetChips>
          {PRESETS.map((preset) => (
            <Chip
              key={preset.id}
              $active={activePresetId === preset.id}
              title={preset.source}
              onClick={() => applyPreset(preset.id)}
            >
              {preset.name}
            </Chip>
          ))}
          {activePresetId === 'custom' && <Chip $active>自定义</Chip>}
        </PresetChips>
        <SectionLabel>背景色（点击选择后下方取色）</SectionLabel>
        {TUNABLE_TOKENS.map(({ name, label }) => (
          <TokenRow
            key={name}
            $active={selectedToken === name}
            onClick={() => setSelectedToken(name)}
          >
            <Swatch style={{ background: effective[name] }} />
            <TokenLabel>{label}</TokenLabel>
            <HexValue>{effective[name]}</HexValue>
          </TokenRow>
        ))}
        <PickerSection>
          <PickerTitle>
            {TUNABLE_TOKENS.find(({ name }) => name === selectedToken)?.label} · {selectedToken}
          </PickerTitle>
          <ColorPicker value={effective[selectedToken]} onChange={handleTokenColorChange} />
        </PickerSection>
      </PanelBody>
      {importing && (
        <ImportSection>
          <ImportTextarea
            autoFocus
            placeholder={'粘贴 JSON，如 {\n  "--fett-bg-app": "#0F1115",\n  ...\n}'}
            value={importText}
            spellCheck={false}
            onChange={(event) => {
              setImportText(event.target.value);
              setImportError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) handleImportApply();
            }}
          />
          {importError && <ImportError>{importError}</ImportError>}
          <ImportActions>
            <FooterButton onClick={handleImportApply}>应用（⌘/Ctrl+Enter）</FooterButton>
            <FooterButton
              onClick={() => {
                setImporting(false);
                setImportText('');
                setImportError(null);
              }}
            >
              取消
            </FooterButton>
          </ImportActions>
        </ImportSection>
      )}
      <PanelFooter>
        <FooterButton onClick={handleCopy}>
          <CopyOutlined />
          {copied ? '已复制' : '复制JSON'}
        </FooterButton>
        <FooterButton
          onClick={() => {
            setImporting((prev) => !prev);
            setImportError(null);
          }}
        >
          <ImportOutlined />
          导入JSON
        </FooterButton>
        <FooterButton onClick={handleReset}>
          <ReloadOutlined />
          重置默认
        </FooterButton>
      </PanelFooter>
    </Panel>
  );
};

export default ColorLabTuner;
